import React from "react";
import { trackAdView, trackAdClick, adRedirectUrl } from "@/api/analytics";
import { resolveMediaUrl } from "@/utils/apiBase";

type Ad = {
  id?: string;
  title?: string;
  description?: string;
  url?: string;
  href?: string;
  image?: string;
  banner?: string;
  cover?: string;
  isActive?: boolean;
};

export interface AdsCarouselProps {
  ads?: Ad[];
  accent?: string; // used for label tint
  cardHeight?: number; // inner card height (without label/indicators)
  lockHeight?: boolean; // when true, reserve a stable min-height to avoid layout shift
}

/**
 * Lightweight ads carousel used in dynamic template previews.
 * - Auto-rotates every 5s
 * - Keeps a stable reserved height to prevent layout shifts
 * - Graceful placeholder when no active ads
 */
export const AdsCarousel: React.FC<AdsCarouselProps> = ({ ads, accent = "#6b7280", cardHeight = 176, lockHeight = true }) => {
  const list = Array.isArray(ads) ? ads : [];
  const activeAds = list.filter((a) => a && (a.isActive !== false));
  const [idx, setIdx] = React.useState(0);
  const viewedRef = React.useRef<Set<string>>(new Set());
  const containerRef = React.useRef<HTMLDivElement|null>(null);
  const [inView, setInView] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (activeAds.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % activeAds.length), 5000);
    return () => clearInterval(t);
  }, [activeAds.length]);

  // Observe visibility of the carousel to avoid counting views when offscreen
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setInView(true); // fallback: assume visible
      return;
    }
    const io = new IntersectionObserver((entries) => {
      const entry = entries[0];
      setInView(Boolean(entry && (entry.isIntersecting || entry.intersectionRatio > 0.2)));
    }, { threshold: [0, 0.25, 0.5, 1] });
    io.observe(el);
    return () => { try { io.disconnect(); } catch {} };
  }, []);

  // Send an impression when the current ad is visible (once per ad per mount)
  React.useEffect(() => {
    const ad = activeAds[idx];
    const id = (ad as any)?.id || (ad as any)?._id;
    if (!id) return;
    if (!inView) return;
    if (viewedRef.current.has(String(id))) return;
    viewedRef.current.add(String(id));
    try { trackAdView(String(id)).catch(()=>{}); } catch {}
  }, [idx, activeAds, inView]);

  const ad = activeAds[idx];
  // Support multiple field names for destination link
  const href = (ad as any)?.url || (ad as any)?.href || (ad as any)?.link;
  // Normalize image URL: accept absolute, /uploads, or relative paths
  const rawImg = ad?.image || ad?.banner || ad?.cover;
  const img = (() => {
    const s = String(rawImg || '').trim();
    if (!s) return undefined;
    if (/^(https?:\/\/|blob:|data:)/i.test(s)) return s;
    if (s.startsWith('/uploads/') || s.startsWith('/images/') || s.startsWith('/assets/')) return s;
    if (s.startsWith('uploads/')) return `/${s}`;
    return resolveMediaUrl(s);
  })();
  const title = ad?.title || "Sponsored";
  // Show title and a short description (2 lines) in the panel

  // Reserve a stable vertical space: label (~16px) + gap (8px) + dots (16px) + cardHeight
  const MIN_CONTENT_H = cardHeight + 16 + 8 + 16; // conservative to avoid jumps

  return (
  <div ref={containerRef} className="px-4 pb-4 mt-5" style={lockHeight ? { minHeight: MIN_CONTENT_H } : undefined}>
      {/* Label */}
      <div className="text-[11px] font-medium text-gray-500 mb-1" style={{ color: accent }}>{"Sponsored"}</div>
      {/* Card */}
      {ad ? (
        <a
          href={(() => {
            const id = (ad as any)?.id || (ad as any)?._id;
            if (id && href) {
              // Use redirect trampoline so redirects are tracked separately
              return adRedirectUrl(String(id), href);
            }
            return href || '#';
          })()}
          target={href ? '_blank' : undefined}
          rel={href ? 'noreferrer' : undefined}
          onClick={(e) => {
            const id = (ad as any)?.id || (ad as any)?._id;
            if (id) {
              try { trackAdClick(String(id)).catch(()=>{}); } catch {}
            }
          }}
          className="block rounded-xl border overflow-hidden bg-white"
        >
          <div className="p-3" style={{ height: cardHeight }}>
            {img ? (
              <img src={img} alt={title} loading="lazy" className="w-full h-24 object-cover rounded-lg mb-2" />
            ) : (
              <div className="w-full h-20 rounded-lg bg-gray-200 mb-2" />
            )}
            <div className="text-sm font-semibold text-gray-800 line-clamp-1" title={title}>{title}</div>
            <div className="text-xs text-gray-600 line-clamp-2 mt-0.5">{ad?.description || 'Sponsored content'}</div>
          </div>
        </a>
      ) : (
        <div className="rounded-xl border bg-gray-50 p-3" style={{ height: cardHeight }}>
          <div className="w-full h-24 rounded-lg bg-gray-200 mb-2" />
          <div className="h-3 w-2/3 rounded bg-gray-200 mb-1" />
          <div className="h-3 w-5/6 rounded bg-gray-200" />
        </div>
      )}
      {/* Dots */}
      <div className="flex justify-center mt-2 h-4 items-center">
        {activeAds.length > 1 ? (
          activeAds.map((_, i) => (
            <div key={i} className={`w-2 h-2 rounded-full mx-0.5 ${i === idx ? 'bg-gray-600' : 'bg-gray-300'}`} />
          ))
        ) : (
          <div className="w-2 h-2 rounded-full opacity-0" />
        )}
      </div>
    </div>
  );
};

export default AdsCarousel;
